import { useCallback, useEffect, useMemo, useState } from 'react';
import { createProviderCredentialsApiService } from '../services/ProviderCredentialsApiService';
import type {
  ProviderCredentialEnvironment,
  ProviderCredentialProviderStatus,
} from '../types';

export interface UseProviderCredentialsResult {
  environment: ProviderCredentialEnvironment | null;
  providers: ProviderCredentialProviderStatus[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  saveProvider: (providerId: string, fields: Record<string, string>) => Promise<void>;
  deleteField: (providerId: string, fieldKey: string) => Promise<void>;
}

function normalizeProvider(provider: ProviderCredentialProviderStatus): ProviderCredentialProviderStatus {
  return {
    ...provider,
    fields: Array.isArray(provider.fields) ? provider.fields : [],
  };
}

function normalizeProviders(
  providers: ProviderCredentialProviderStatus[] | undefined,
): ProviderCredentialProviderStatus[] {
  if (!Array.isArray(providers)) {
    return [];
  }
  return providers.map(normalizeProvider);
}

export function useProviderCredentials(): UseProviderCredentialsResult {
  const service = useMemo(() => createProviderCredentialsApiService(), []);
  const [environment, setEnvironment] = useState<ProviderCredentialEnvironment | null>(null);
  const [providers, setProviders] = useState<ProviderCredentialProviderStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = await service.getStatus();
      setEnvironment(status.environment);
      setProviders(normalizeProviders(status.providers));
      if (!Array.isArray(status.providers)) {
        setError('Provider credentials API response is missing providers. Deploy the latest builder-service.');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const replaceProvider = useCallback((provider: ProviderCredentialProviderStatus) => {
    const normalizedProvider = normalizeProvider(provider);
    setProviders((current) =>
      current.map((candidate) =>
        candidate.id === normalizedProvider.id ? normalizedProvider : candidate,
      ),
    );
  }, []);

  const saveProvider = useCallback(
    async (providerId: string, fields: Record<string, string>) => {
      setSaving(true);
      setError(null);
      try {
        const result = await service.saveProvider(providerId, fields);
        setEnvironment(result.environment);
        replaceProvider(result.provider);
      } catch (err) {
        setError((err as Error).message);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [replaceProvider, service],
  );

  const deleteField = useCallback(
    async (providerId: string, fieldKey: string) => {
      setSaving(true);
      setError(null);
      try {
        const result = await service.deleteField(providerId, fieldKey);
        setEnvironment(result.environment);
        replaceProvider(result.provider);
      } catch (err) {
        setError((err as Error).message);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [replaceProvider, service],
  );

  return {
    environment,
    providers,
    loading,
    saving,
    error,
    refresh,
    saveProvider,
    deleteField,
  };
}
